import {
  lockBody,
  unlockBody,
  focusElement,
  head,
  last,
  addClass,
  removeClass,
  closer,
  matcher,
} from './lib';
import { BOOLEAN_STRING, MAGIC_NUMBERS, ARIA } from './constants';
import { debounce, throttle } from 'throttle-debounce';
import { closeSearch } from './headerSearch';
import {
  currentScrollPosition,
  setLastScrollPosition,
  isScrollingDown,
  isScrollingUp,
} from './scroll';
import {
  keyPressIsEscape,
  keyPressIsArrowUp,
  a11yOpenkeyMatcher,
} from './keys';

const MENU_HEADER_ID = 'js-menu-header';
const MENU_TOGGLE_BUTTON_ID = 'js-smaka-menu-toggle-button';
const MENU_TOGGLE_BUTTON_CLOSER = closer(`#${MENU_TOGGLE_BUTTON_ID}`);
const MENU_TOGGLE_BUTTON_MATCHER = matcher(`#${MENU_TOGGLE_BUTTON_ID}`);
const MENU_ID = 'js-smaka-menu';
const HEADER_OPEN_STATE_CLASS = 'l-header--open';
const MENU_COLLAPSED_CLASS = 'menu-collapsed';
const MENU_EXPANDING_CLASS = 'menu-expanding';

const menuButtonElement = () => document.getElementById(MENU_TOGGLE_BUTTON_ID);

export const menuHeaderElement = () => document.getElementById(MENU_HEADER_ID);

const menuChildrenElements = () =>
  Array.from(document.getElementById(MENU_ID).children);

const menuExpandedState = () => menuButtonElement().getAttribute(ARIA.EXPANDED);

const menuIsCollapsed = () =>
  menuHeaderElement().dataset.menuIsCollapsed === BOOLEAN_STRING.TRUE;

const openMenu = () => {
  menuButtonElement().setAttribute(ARIA.EXPANDED, BOOLEAN_STRING.TRUE);
  menuHeaderElement().classList.add(HEADER_OPEN_STATE_CLASS);
  closeSearch();
  lockBody();
};

export const closeMenu = () => {
  menuButtonElement().setAttribute(ARIA.EXPANDED, BOOLEAN_STRING.FALSE);
  menuHeaderElement().classList.remove(HEADER_OPEN_STATE_CLASS);
  unlockBody();
};

const toggleMenu = () => {
  menuExpandedState() === BOOLEAN_STRING.TRUE ? closeMenu() : openMenu();
};

const headerClickHandler = event => {
  if (MENU_TOGGLE_BUTTON_CLOSER(event)) toggleMenu();
};

const headerKeyupHandler = debounce(MAGIC_NUMBERS.DEBOUNCE_INTERVAL, event => {
  if (keyPressIsEscape(event)) {
    closeMenu();
    focusElement(menuButtonElement());
  }

  if (a11yOpenkeyMatcher(event) && MENU_TOGGLE_BUTTON_MATCHER(event)) {
    openMenu();
    focusElement(head(menuChildrenElements()));
  }

  if (keyPressIsArrowUp(event) && MENU_TOGGLE_BUTTON_MATCHER(event)) {
    openMenu();
    focusElement(last(menuChildrenElements()));
  }
});

const mediaQueryListFromResizeBreakpoint = () =>
  window.matchMedia(`(min-width: ${MAGIC_NUMBERS.RESIZE_BREAKPOINT}px)`);

const closeSearchOnlyOnLargeDevice = () => {
  if (mediaQueryListFromResizeBreakpoint().matches) closeSearch();
};

const hasScrolledBeyondCollapsePoint = () =>
  currentScrollPosition() >= MAGIC_NUMBERS.MENU_COLLAPSE_SCROLL_POINT;

const hasScrolledToTop = () =>
  currentScrollPosition() <= MAGIC_NUMBERS.MENU_EXPAND_SCROLL_POINT;

const hasScrolledBeyondExpandPoint = () =>
  currentScrollPosition() <= MAGIC_NUMBERS.MENU_EXPANDING_SCROLL_POINT;

const shouldCollapse = () =>
  hasScrolledBeyondCollapsePoint() && (!menuIsCollapsed() || isScrollingDown());

const shouldStartExpanding = () =>
  hasScrolledBeyondExpandPoint() && menuIsCollapsed() && isScrollingUp();

const collapseMenu = () => {
  addClass(MENU_COLLAPSED_CLASS)(document.body);
  removeClass(MENU_EXPANDING_CLASS)(document.body);
  menuHeaderElement().dataset.menuIsCollapsed = BOOLEAN_STRING.TRUE;
};

const expandMenu = () => {
  closeSearchOnlyOnLargeDevice();
  removeClass(MENU_COLLAPSED_CLASS)(document.body);
  removeClass(MENU_EXPANDING_CLASS)(document.body);
  menuHeaderElement().dataset.menuIsCollapsed = BOOLEAN_STRING.FALSE;
};

const startExpandingMenu = () => {
  addClass(MENU_EXPANDING_CLASS)(document.body);
  removeClass(MENU_COLLAPSED_CLASS)(document.body);
  closeSearchOnlyOnLargeDevice();
};

const headerScrollHandler = event => {
  if (shouldCollapse()) {
    collapseMenu();
  } else if (hasScrolledToTop()) {
    expandMenu();
  } else if (shouldStartExpanding()) {
    startExpandingMenu();
  }

  setLastScrollPosition();
};

/**
 * Closes mobile menu when media query matches.
 * @param {MediaQueryList} mq
 */
const handleCloseMenuWidthChange = mq => mq.matches && closeMenu();

export function init() {
  menuHeaderElement().addEventListener('click', headerClickHandler);
  document.addEventListener('keyup', headerKeyupHandler);
  document.addEventListener(
    'scroll',
    throttle(MAGIC_NUMBERS.THROTTLE_INTERVAL, headerScrollHandler),
  );

  mediaQueryListFromResizeBreakpoint().addListener(handleCloseMenuWidthChange);
}
